import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from '@pantry/ui';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface UpgradeDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog requests to close */
  onClose: () => void;
  /** Human-readable feature name */
  feature: string;
  /** What the user is missing */
  description?: string;
  /** Called when the user clicks the upgrade CTA */
  onUpgrade?: () => void;
}

/**
 * Modal dialog variant of the upgrade prompt.
 *
 * Shown when a user tries to perform a premium action inline —
 * e.g., creating a 2nd shopping list or pinning a list.
 *
 * All strings are internationalized with Lingui.
 *
 * @example
 * ```tsx
 * <UpgradeDialog
 *   open={showUpgradeDialog}
 *   onClose={() => setShowUpgradeDialog(false)}
 *   feature="Multiple Shopping Lists"
 *   description="Create unlimited lists for different stores or meal plans."
 *   onUpgrade={() => navigate('/pricing')}
 * />
 * ```
 */
export function UpgradeDialog({
  open,
  onClose,
  feature,
  description,
  onUpgrade,
}: UpgradeDialogProps) {
  const { t } = useLingui();

  const handleUpgrade = () => {
    onUpgrade?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md overflow-hidden p-0">
        {/* Decorative gradient header */}
        <div className="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600" />

        <div className="p-6">
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/40">
                  <SparklesIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <DialogTitle className="font-display text-lg">
                    {t({ message: 'Premium Feature', context: 'dialog_title' })}
                  </DialogTitle>
                  <DialogDescription className="text-sm font-medium text-foreground mt-0.5">
                    {feature}
                  </DialogDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="shrink-0 h-8 w-8 p-0"
                aria-label={t({ message: 'Close dialog', context: 'aria_label' })}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          {description && <p className="text-sm text-muted-foreground mb-3">{description}</p>}

          <p className="text-sm text-muted-foreground mb-6">
            <Trans>
              Upgrade to unlock this feature and get access to all premium capabilities.
            </Trans>
          </p>

          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              <Trans context="action_button">Maybe later</Trans>
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-700"
            >
              <SparklesIcon className="h-4 w-4 mr-2" />
              <Trans context="action_button">Upgrade to Premium</Trans>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
